import React, { useState, useEffect } from 'react';
import { Plus, Trash2, AlertCircle, PencilLine } from 'lucide-react';
import { 
  useGetSensorListQuery, 
  useGetTypesQuery,
  useAddSensorMutation,
  useDeleteSensorMutation,
  useUpdateSensorMutation
} from '../services/sensorApi';
import { useGetAlarmLogQuery } from '../services/alarmApi';
import { formatSensorSerial } from '../utils/sensorUtils';
import { getBatteryStatus } from '../utils/batteryUtils';
import { useGetZonesQuery, useAssignSensorMutation, useRemoveSensorMutation } from '../services/zoneApi';
import { Sensor } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import SensorTable from '../components/sensors/SensorTable';
import { Dialog } from '../components/common/Dialog';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useGetBaseUnitListQuery } from '../services/baseUnitApi';
import axios from 'axios';
import { isNewerVersion } from '../utils/sensorUtils';

interface EditSensorState {
  id: number | null;
  name: string;
}

interface AddSensorForm {
  serialNo: string;
  name: string;
  type: string;
  zoneId?: number;
}

const initialFormState: AddSensorForm = {
  serialNo: '',
  name: '',
  type: '1'
};

interface ApiError {
  data?: {
    message?: string;
  };
  message?: string;
}

export default function SensorsPage() {
  const { canManageSensors } = usePermissions();
  const [isAddingSensor, setIsAddingSensor] = useState(false);
  const [formData, setFormData] = useState<AddSensorForm>(initialFormState);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingSensor, setEditingSensor] = useState<EditSensorState>({ id: null, name: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [serialError, setSerialError] = useState<string | null>(null);
  const [isUpdateFirmwareModalOpen, setIsUpdateFirmwareModalOpen] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState<number | null>(null);
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdateAllModalOpen, setIsUpdateAllModalOpen] = useState(false);
  const [latestFirmwareVersion, setLatestFirmwareVersion] = useState<string | null>(null);
  const [updateAllStatus, setUpdateAllStatus] = useState<string>('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<{ id: number | null; name: string; type: string }>({ id: null, name: '', type: '' });
  
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: baseUnits } = useGetBaseUnitListQuery();

  const { data: sensors, isLoading, error, refetch: refetchSensors } = useGetSensorListQuery();
  const { data: types } = useGetTypesQuery();
  const [addSensor] = useAddSensorMutation();
  const [deleteSensor] = useDeleteSensorMutation();
  const [updateSensor] = useUpdateSensorMutation();
  const { data: zones } = useGetZonesQuery();
  const [assignSensor] = useAssignSensorMutation();
  const [removeSensor] = useRemoveSensorMutation();
  const { data: alarmLog } = useGetAlarmLogQuery();

  useEffect(() => {
    const fetchLatestFirmware = async () => {
      try {
        const response = await fetch('https://raw.githubusercontent.com/thermalsystemsltd/sensorfirmware/refs/heads/main/version');
        const version = await response.text();
        setLatestFirmwareVersion(version.trim());
      } catch (e) {
        console.error("Failed to fetch latest firmware version", e);
      }
    };
    fetchLatestFirmware();
  }, []);

  const updatableSensors = React.useMemo(() => {
    if (!sensors || !latestFirmwareVersion) return [];
    return sensors.filter(s => s.type === '1' && isNewerVersion(latestFirmwareVersion, s.firmware_version || ''));
  }, [sensors, latestFirmwareVersion]);

  const handleUpdateFirmware = (sensorId: number) => {
    setSelectedSensor(sensorId);
    setIsUpdateFirmwareModalOpen(true);
  };

  const handleFirmwareUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError(null);

    const sensorToUpdate = sensors?.find(s => s.id === selectedSensor);
    if (!sensorToUpdate) {
      setFormError('Could not find the selected sensor.');
      setIsSubmitting(false);
      return;
    }

    const ts1BaseUnits = baseUnits?.filter(bu => bu.type === 1).map(bu => bu.serialNo.toString());

    const payload = {
      email: user?.email,
      sensorSerialNumber: sensorToUpdate.serialNo.toString(),
      baseUnitSerialNumbers: ts1BaseUnits,
      wifi: {
        ssid: wifiSsid,
        password: wifiPassword,
      }
    };

    try {
      const response = await axios.post('https://n8n.hookedonflies.app/webhook/81ebd468-52c3-4579-81bd-6f13b22d4038', payload, {
        headers: {
          'Content-Type': 'application/json',
          'x-company-name': user?.companyName || '',
          'x-company-id': '1',
        }
      });

      if (response.status !== 200) {
        throw new Error('Failed to send firmware update request.');
      }

      alert('Firmware update request sent successfully!');
      closeFirmwareUpdateModal();

    } catch (error: any) {
      setFormError(error.message || 'An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeFirmwareUpdateModal = () => {
    setIsUpdateFirmwareModalOpen(false);
    setSelectedSensor(null);
    setWifiSsid('');
    setWifiPassword('');
  };

  const filteredSensors = React.useMemo(() => {
    if (!sensors) return [];
    if (!searchQuery.trim()) return sensors;

    const query = searchQuery.toLowerCase();
    return sensors.filter(sensor => 
      sensor.name.toLowerCase().includes(query) || 
      formatSensorSerial(sensor.serialNo, sensor.type).toLowerCase().includes(query)
    );
  }, [sensors, searchQuery]);

  const handleEdit = (sensor: Sensor) => {
    if (!canManageSensors) return;
    setEditFormData({ id: sensor.id, name: sensor.name, type: sensor.type });
    setIsEditModalOpen(true);
  };

  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditModalSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFormData.id) return;
    setFormError(null);
    try {
      await updateSensor({
        sensorId: editFormData.id,
        sensorName: editFormData.name
      }).unwrap();
      setIsEditModalOpen(false);
      setEditFormData({ id: null, name: '', type: '' });
      setEditingSensor({ id: null, name: '' });
    } catch (error) {
      let errorMessage = 'Failed to update sensor. Please try again.';
      if (typeof error === 'object' && error !== null && 'data' in error && (error as any).data?.message) {
        errorMessage = (error as any).data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      setFormError(errorMessage);
      console.error('Update sensor error:', error);
    }
  };

  const handleEditModalCancel = () => {
    setIsEditModalOpen(false);
    setEditFormData({ id: null, name: '', type: '' });
    setEditingSensor({ id: null, name: '' });
  };

  const getSerialPattern = (type: string) => {
    if (type === 'TS1' || type === '4') return /^\d{2}\.\d{2}\.\d{2}$/;
    if (type === 'EMS IceSpy' || type === 'EMS IceSpy Humidity' || type === '2' || type === '3') return /^\d{2}\.\d{2}\.\d{2}\.\d{2}$/;
    return null;
  };

  const getSerialPlaceholder = (type: string) => {
    if (type === 'TS1' || type === '4') return 'e.g. 12.34.56';
    if (type === 'EMS IceSpy' || type === 'EMS IceSpy Humidity' || type === '2' || type === '3') return 'e.g. 12.34.56.78';
    return '';
  };

  const validateSerial = (serial: string, type: string) => {
    const pattern = getSerialPattern(type);
    if (!pattern) return true;
    return pattern.test(serial);
  };

  const maskSerialInput = (value: string, type: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    if (type === 'TS1' || type === '4') {
      // xx.xx.xx (6 digits)
      return digits
        .slice(0, 6)
        .replace(/(\d{2})(\d{2})(\d{0,2})/, (m, p1, p2, p3) => p3 ? `${p1}.${p2}.${p3}` : `${p1}.${p2}`.replace(/\.$/, ''));
    }
    if (type === 'EMS IceSpy' || type === 'EMS IceSpy Humidity' || type === '2' || type === '3') {
      // xx.xx.xx.xx (8 digits)
      return digits
        .slice(0, 8)
        .replace(/(\d{2})(\d{2})(\d{2})(\d{0,2})/, (m, p1, p2, p3, p4) => p4 ? `${p1}.${p2}.${p3}.${p4}` : `${p1}.${p2}.${p3}`.replace(/\.$/, ''));
    }
    return digits;
  };

  const handleSerialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = maskSerialInput(value, formData.type);
    setFormData({ ...formData, serialNo: value });
    if (formData.type) {
      if (!validateSerial(value, formData.type)) {
        setSerialError('Serial number format is invalid for the selected sensor type.');
      } else {
        setSerialError(null);
      }
    } else {
      setSerialError(null);
    }
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({ ...formData, type: e.target.value, serialNo: '' });
    setSerialError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageSensors) return;
    setFormError(null);
    if (!formData.type) {
      setSerialError('Please select a sensor type first.');
      return;
    }
    if (!validateSerial(formData.serialNo, formData.type)) {
      setSerialError('Serial number format is invalid for the selected sensor type.');
      return;
    }
    setSerialError(null);

    try {
      // Strip all dots from the serial number before sending
      const serialNumberStripped = formData.serialNo.replace(/\./g, '');
      const serialNumber = parseInt(serialNumberStripped);
      if (isNaN(serialNumber)) {
        throw new Error('Serial number must be a valid number');
      }

      // Create the sensor
      await addSensor({
        serialNo: serialNumber,
        name: formData.name,
        type: formData.type
      }).unwrap();

      // Wait a brief moment for the database to update
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Replace the refetch and sensors usage with:
      const { data: sensorsList } = await refetchSensors();
      const newSensor = sensorsList?.find((s: Sensor) => s.serialNo === serialNumber);
      
      if (!newSensor) {
        throw new Error('Could not find newly created sensor');
      }

      if (formData.zoneId) {
        console.log('Attempting to assign sensor', newSensor.id, 'to zone', formData.zoneId);
        try {
          await assignSensor({
            zoneId: formData.zoneId,
            sensorId: newSensor.id
          }).unwrap();
          console.log('Successfully assigned sensor to zone');
        } catch (zoneError: any) {
          console.error('Zone assignment error:', zoneError);
          let errorMessage = 'Failed to assign sensor to zone';
          if (typeof zoneError === 'object' && zoneError !== null && 'data' in zoneError && (zoneError as any).data?.message) {
            errorMessage = (zoneError as any).data.message;
          } else if (zoneError instanceof Error) {
            errorMessage = zoneError.message;
          }
          setFormError(errorMessage);
          return;
        }
      }

      setFormData(initialFormState);
      setIsAddingSensor(false);
    } catch (error: unknown) {
      console.error('Error in handleSubmit:', error);
      const err = error as ApiError;
      const errorMessage = err?.data?.message || err?.message || 'Failed to add sensor';
      setFormError(errorMessage);
    }
  };

  const handleDelete = async (sensor: Sensor) => {
    if (!canManageSensors) return;
    if (window.confirm('Are you sure you want to delete this sensor?')) {
      try {
        setFormError(null);
        
        // First, remove the sensor from all zones
        if (zones) {
          for (const zone of zones) {
            try {
              await removeSensor({ zoneId: zone.zone_id, sensorId: sensor.id }).unwrap();
            } catch (error) {
              console.error(`Failed to remove sensor from zone ${zone.zone_id}:`, error);
              // Continue with other zones even if one fails
            }
          }
        }

        // Then delete the sensor
        await deleteSensor(sensor).unwrap();
      } catch (error) {
        console.error('Failed to delete sensor:', error);
        let errorMessage = 'Failed to delete sensor. Please try again.';
        if (typeof error === 'object' && error !== null && 'data' in error && (error as any).data?.message) {
          errorMessage = (error as any).data.message;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
        setFormError(errorMessage);
      }
    }
  };

  const handleUpdateAll = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setUpdateAllStatus(`Starting update for ${updatableSensors.length} sensors...`);

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < updatableSensors.length; i++) {
      const sensor = updatableSensors[i];
      setUpdateAllStatus(`Updating sensor ${i + 1} of ${updatableSensors.length}: ${sensor.name}`);
      
      const payload = {
        email: user?.email,
        sensorSerialNumber: sensor.serialNo.toString(),
        baseUnitSerialNumbers: baseUnits?.filter(bu => bu.type === 1).map(bu => bu.serialNo.toString()),
        wifi: { ssid: wifiSsid, password: wifiPassword }
      };

      try {
        await axios.post('https://n8n.hookedonflies.app/webhook/81ebd468-52c3-4579-81bd-6f13b22d4038', payload, {
          headers: {
            'Content-Type': 'application/json',
            'x-company-name': user?.companyName || '',
            'x-company-id': '1',
          }
        });
        successCount++;
        // Optional delay between requests
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Failed to update sensor ${sensor.serialNo}`, error);
        failCount++;
      }
    }

    setUpdateAllStatus(`Update complete. Successful: ${successCount}, Failed: ${failCount}.`);
    setIsSubmitting(false);
    // Optionally close the modal after a delay
    setTimeout(() => {
      setIsUpdateAllModalOpen(false);
      setUpdateAllStatus('');
    }, 5000);
  };

  if (isLoading) {
    return <div className="p-6">Loading sensors...</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-700 dark:text-red-400">Failed to load sensors</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Sensors</h2>
          <p className="mt-1 mb-4 text-sm text-gray-500 dark:text-gray-400">
            {canManageSensors ? 'Manage your temperature sensors' : 'View temperature sensors'}
          </p>
          <div className="relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-96 pl-3 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                ×
              </button>
            )}
          </div>
        </div>
        {canManageSensors && (
          <button
            onClick={() => setIsAddingSensor(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white p-2 sm:px-4 sm:py-2 rounded-md flex items-center text-sm"
          >
            <Plus className="h-5 w-5 mr-2" />
            <span className="hidden sm:inline">Add Sensor</span>
          </button>
        )}
      </div>

      {canManageSensors && updatableSensors.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => setIsUpdateAllModalOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white p-2 sm:px-4 sm:py-2 rounded-md flex items-center text-sm"
            disabled={isSubmitting}
          >
            <Plus className="h-5 w-5 mr-2" />
            <span className="hidden sm:inline">Update All ({updatableSensors.length})</span>
          </button>
        </div>
      )}

      {isAddingSensor && (
        <div className="mb-6 bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Add New Sensor</h3>
          {formError && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Sensor Type
              </label>
              <select
                value={formData.type}
                onChange={handleTypeChange}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-white"
              >
                <option value="">Select type</option>
                {types?.map((type) => (
                  <option key={type.type} value={type.type}>
                    {type.description}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Serial Number
              </label>
              <input
                type="text"
                required
                value={formData.serialNo}
                onChange={handleSerialChange}
                disabled={!formData.type}
                placeholder={getSerialPlaceholder(formData.type)}
                className={`mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-white ${serialError ? 'border-red-500' : ''}`}
              />
              {serialError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{serialError}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Sensor Name
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Assign to Zone (Optional)
              </label>
              <select
                value={formData.zoneId || ''}
                onChange={(e) => setFormData({ ...formData, zoneId: e.target.value ? Number(e.target.value) : undefined })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-white"
              >
                <option value="">Select zone</option>
                {zones?.map((zone) => (
                  <option key={zone.zone_id} value={zone.zone_id}>
                    {zone.zone_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsAddingSensor(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
              >
                Add Sensor
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <SensorTable
            sensors={filteredSensors}
            alarmLog={alarmLog || []}
            onUpdateFirmware={handleUpdateFirmware}
            showFirmwareUpdate={true}
            latestFirmwareVersion={latestFirmwareVersion}
            onEdit={handleEdit}
            onDelete={canManageSensors ? handleDelete : undefined}
            enableDragAndDrop={false}
          />
        </div>
      </div>

      <Dialog isOpen={isUpdateFirmwareModalOpen} onClose={closeFirmwareUpdateModal}>
        <form onSubmit={handleFirmwareUpdateSubmit}>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Update Sensor Firmware</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            The sensor requires Wi-Fi credentials to download the latest firmware update.
            This information is used once for the update and is not stored.
          </p>
          {formError && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                WiFi SSID
              </label>
              <input
                type="text"
                required
                value={wifiSsid}
                onChange={(e) => setWifiSsid(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                WiFi Password
              </label>
              <input
                type="password"
                required
                value={wifiPassword}
                onChange={(e) => setWifiPassword(e.target.value)}
                disabled={isSubmitting}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-white"
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={closeFirmwareUpdateModal}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md flex items-center"
            >
              {isSubmitting && <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>}
              {isSubmitting ? 'Updating...' : 'Update Firmware'}
            </button>
          </div>
        </form>
      </Dialog>

      <Dialog isOpen={isUpdateAllModalOpen} onClose={() => !isSubmitting && setIsUpdateAllModalOpen(false)}>
        <form onSubmit={handleUpdateAll}>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Update All Sensors ({updatableSensors.length})</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Enter the Wi-Fi credentials for the sensors to use to download the firmware.
          </p>
          {updateAllStatus ? (
            <div className="my-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md text-sm text-blue-700 dark:text-blue-300">
              {updateAllStatus}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">WiFi SSID</label>
                <input
                  type="text"
                  required
                  value={wifiSsid}
                  onChange={(e) => setWifiSsid(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-white"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">WiFi Password</label>
                <input
                  type="password"
                  required
                  value={wifiPassword}
                  onChange={(e) => setWifiPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-white"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          )}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setIsUpdateAllModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md flex items-center"
              disabled={isSubmitting || !!updateAllStatus}
            >
              {isSubmitting && <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>}
              {isSubmitting ? 'Updating...' : `Start Update`}
            </button>
          </div>
        </form>
      </Dialog>

      {/* Edit Sensor Modal */}
      <Dialog isOpen={isEditModalOpen} onClose={handleEditModalCancel}>
        <form onSubmit={handleEditModalSave} className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Edit Sensor</h3>
          {formError && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Sensor Name</label>
            <input
              type="text"
              name="name"
              required
              value={editFormData.name}
              onChange={handleEditFormChange}
              className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Sensor Type</label>
            <select
              name="type"
              value={editFormData.type}
              onChange={handleEditFormChange}
              className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-white"
            >
              <option value="">Select type</option>
              {types?.map((type) => (
                <option key={type.type} value={type.type}>
                  {type.description}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleEditModalCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
            >
              Save
            </button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}